import { useEffect, useMemo, useState } from "react";

import {
  Badge,
  Box,
  Button,
  Flex,
  InfoBox,
  Select,
  Switch,
  Table,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import {
  AiDifficultyProfile,
  AiModel,
  DifficultyTierName,
  RoutingModeName,
  aiGatewayService,
  difficultyTierValue,
  difficultyTiers,
  routingModeValue,
} from "services/aiGatewayService";
import { getThemeColor } from "theme/colors";

import { routeDisplayName, routeInheritanceHint } from "./routeLabel";

interface DifficultyPanelProps {
  profile: AiDifficultyProfile | null;
  models: AiModel[];
  loading: boolean;
  onChanged: () => void;
}

const UNSET = "__unset__";

/**
 * Difficulty tier map — the "Standard Mode" of the two routing modes.
 *
 * Every agent route carries a tier, and each tier picks one model. This sits below a per-route
 * binding in the resolution chain, so it is the answer for "cheap model for the cheap work"
 * without having to name every route individually.
 *
 * Naming note: the switch below is per-question routing (RoutingMode) and is NOT the "Advanced
 * Mode" tab (AiRouteBinding). They are different steps of the resolution chain and the copy has to
 * keep them apart, because a user who conflates them expects the switch to change the other tab.
 *
 * Dynamic (per-question) routing classifies each turn before answering it, so a "thanks" runs on
 * the cheap tier and a multi-step request can escalate. It costs one extra small LLM call per turn
 * that is not caught by the short-message fast path, which is why it is opt-in and says so.
 * `dynamicRoutingAvailable` is the deployment's kill switch: when it is off the toggle is disabled
 * rather than hidden, because the setting exists — it is just not live here.
 */
const DifficultyPanel = ({
  profile,
  models,
  loading,
  onChanged,
}: DifficultyPanelProps) => {
  const { t } = useTranslation();

  const [simpleModelId, setSimpleModelId] = useState(UNSET);
  const [moderateModelId, setModerateModelId] = useState(UNSET);
  const [complexModelId, setComplexModelId] = useState(UNSET);
  const [classifierTier, setClassifierTier] =
    useState<DifficultyTierName>("Simple");
  const [routingMode, setRoutingMode] =
    useState<RoutingModeName>("StaticStage");
  const [escalationEnabled, setEscalationEnabled] = useState(false);
  const [escalateMainTo, setEscalateMainTo] =
    useState<DifficultyTierName>("Complex");
  const [tierByRoute, setTierByRoute] = useState<
    Record<string, DifficultyTierName>
  >({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setSimpleModelId(profile.simpleAiModelId ?? UNSET);
    setModerateModelId(profile.moderateAiModelId ?? UNSET);
    setComplexModelId(profile.complexAiModelId ?? UNSET);
    setClassifierTier(profile.classifierTier);
    setRoutingMode(profile.routingMode);
    setEscalationEnabled(profile.escalationEnabled);
    // Falls back to Complex rather than to null: escalation with no target is a switch that does
    // nothing, and the select has to show something the moment the switch is turned on.
    setEscalateMainTo(profile.escalateMainTo ?? "Complex");
    setTierByRoute(
      Object.fromEntries(profile.routes.map((r) => [r.agentRoute, r.tier])),
    );
  }, [profile]);

  const isDynamic = routingMode === "DynamicQuestion";

  const modelOptions = useMemo(
    () => [
      { label: t("app.aiGateway.difficulty.noModel"), value: UNSET },
      ...models.map((model) => ({
        label: model.connectorName
          ? `${model.displayName} · ${model.connectorName}`
          : `${model.displayName} · ${model.provider}`,
        value: model.id,
      })),
    ],
    [models, t],
  );

  // A tier can stay pointed at a model that has since been retired: the catalog only offers active,
  // non-deprecated rows, and nothing rewrites a tier when a model leaves it. Without an option
  // carrying that id the native select renders blank, and saving the form would silently move the
  // tier onto whatever the browser picks. The extra option is disabled — it says what the tier is
  // set to without offering it as a choice.
  const optionsFor = (currentModelId: string) =>
    currentModelId !== UNSET && !models.some((m) => m.id === currentModelId)
      ? [
          ...modelOptions,
          {
            label: t("app.aiGateway.retiredModel"),
            value: currentModelId,
            disabled: true,
          },
        ]
      : modelOptions;

  const tierOptions = difficultyTiers.map((tier) => ({
    label: t(`app.aiGateway.difficulty.tier.${tier}`),
    value: tier,
  }));

  const tenantRoutes = (profile?.routes ?? []).filter(
    (r) => r.plane === "Tenant",
  );

  const handleSave = async () => {
    if (!profile) return;

    // Only genuine overrides are sent. A route left on its default tier must stay unpinned so a
    // later change to the platform default still reaches this company.
    const tierOverrides: Record<string, number> = {};
    tenantRoutes.forEach((route) => {
      const tier = tierByRoute[route.agentRoute];
      if (tier && tier !== route.defaultTier) {
        tierOverrides[route.agentRoute] = difficultyTierValue[tier];
      }
    });

    setSaving(true);
    try {
      await aiGatewayService.upsertDifficultyProfile({
        routingMode: routingModeValue[routingMode],
        tierOverrides,
        simpleAiModelId: simpleModelId === UNSET ? null : simpleModelId,
        moderateAiModelId: moderateModelId === UNSET ? null : moderateModelId,
        complexAiModelId: complexModelId === UNSET ? null : complexModelId,
        classifierTier: difficultyTierValue[classifierTier],
        // Escalation only means anything once a class is being decided per turn, so static mode
        // saves it off rather than leaving a setting that silently does nothing.
        escalationEnabled: isDynamic && escalationEnabled,
        escalateMainTo:
          isDynamic && escalationEnabled
            ? difficultyTierValue[escalateMainTo]
            : null,
        isActive: true,
      });
      onChanged();
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <Text color={getThemeColor("text.secondary")}>
        {loading
          ? t("app.common.loading")
          : t("app.aiGateway.difficulty.empty")}
      </Text>
    );
  }

  return (
    <VStack align="stretch" gap={5}>
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3}>
        <VStack align="start" gap={1}>
          <Text fontSize="sm" color={getThemeColor("text.secondary")}>
            {t("app.aiGateway.difficulty.subtitle")}
          </Text>
          {profile.companyId === null && (
            <Badge colorPalette="gray" size="sm">
              {t("app.aiGateway.difficulty.inherited")}
            </Badge>
          )}
        </VStack>
        <Button
          intent="primary"
          size="sm"
          loading={saving}
          onClick={handleSave}
        >
          {t("app.common.save")}
        </Button>
      </Flex>

      <VStack align="stretch" gap={3}>
        <Text fontSize="sm" fontWeight="semibold">
          {t("app.aiGateway.difficulty.modeTitle")}
        </Text>
        <Text fontSize="xs" color={getThemeColor("text.secondary")}>
          {t("app.aiGateway.difficulty.modeHelp")}
        </Text>
        <Switch
          checked={isDynamic}
          disabled={!profile.dynamicRoutingAvailable}
          onCheckedChange={(e: { checked: boolean }) =>
            setRoutingMode(e.checked ? "DynamicQuestion" : "StaticStage")
          }
        >
          <VStack align="start" gap={0}>
            <Text fontSize="sm">
              {t("app.aiGateway.difficulty.mode.perQuestion")}
            </Text>
            {/* The helper describes the state the switch is actually in, not always the on-state:
                a user reading "each message is judged first" beside an off switch reads it as a
                description of what the page is doing right now. */}
            <Text fontSize="xs" color={getThemeColor("text.secondary")}>
              {!profile.dynamicRoutingAvailable
                ? t("app.aiGateway.difficulty.mode.dynamicUnavailable")
                : isDynamic
                  ? t("app.aiGateway.difficulty.mode.dynamicHelp")
                  : t("app.aiGateway.difficulty.mode.staticHelp")}
            </Text>
          </VStack>
        </Switch>
      </VStack>

      {isDynamic && (
        <InfoBox variant="warning">
          <VStack align="start" gap={1}>
            <Text fontSize="sm" fontWeight="semibold">
              {t("app.aiGateway.difficulty.dynamic.costTitle")}
            </Text>
            <Text fontSize="sm">
              {t("app.aiGateway.difficulty.dynamic.costBody")}
            </Text>
          </VStack>
        </InfoBox>
      )}

      <VStack align="stretch" gap={3}>
        <Text fontSize="sm" fontWeight="semibold">
          {t("app.aiGateway.difficulty.modelsTitle")}
        </Text>
        <Text fontSize="xs" color={getThemeColor("text.secondary")}>
          {t("app.aiGateway.difficulty.modelsHelp")}
        </Text>
        <Flex gap={4} wrap="wrap" align="flex-start">
          <Box minW="240px" flex="1">
            <Select
              label={t("app.aiGateway.difficulty.tier.Simple")}
              value={[simpleModelId]}
              onValueChange={(d: { value: string[] }) =>
                setSimpleModelId(d.value[0])
              }
              items={optionsFor(simpleModelId)}
              helperText={t("app.aiGateway.difficulty.tierHelp.Simple")}
            />
          </Box>
          <Box minW="240px" flex="1">
            <Select
              label={t("app.aiGateway.difficulty.tier.Moderate")}
              value={[moderateModelId]}
              onValueChange={(d: { value: string[] }) =>
                setModerateModelId(d.value[0])
              }
              items={optionsFor(moderateModelId)}
              helperText={t("app.aiGateway.difficulty.tierHelp.Moderate")}
            />
          </Box>
          <Box minW="240px" flex="1">
            <Select
              label={t("app.aiGateway.difficulty.tier.Complex")}
              value={[complexModelId]}
              onValueChange={(d: { value: string[] }) =>
                setComplexModelId(d.value[0])
              }
              items={optionsFor(complexModelId)}
              helperText={t("app.aiGateway.difficulty.tierHelp.Complex")}
            />
          </Box>
        </Flex>
      </VStack>

      <VStack align="stretch" gap={3}>
        <Text fontSize="sm" fontWeight="semibold">
          {t("app.aiGateway.difficulty.routesTitle")}
        </Text>
        <Text fontSize="xs" color={getThemeColor("text.secondary")}>
          {t("app.aiGateway.difficulty.routesHelp")}
        </Text>
        <Box overflowX="auto">
          <Table.Root size="sm" variant="outline" minW="640px">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>
                  {t("app.aiGateway.table.route")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.table.defaultTier")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.table.tier")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {tenantRoutes.map((route) => (
                <Table.Row key={route.agentRoute}>
                  <Table.Cell>
                    <VStack align="start" gap={0}>
                      <Text fontWeight="medium">
                        {routeDisplayName(t, route)}
                      </Text>
                      <Text
                        fontSize="xs"
                        color={getThemeColor("text.secondary")}
                      >
                        {route.agentRoute}
                      </Text>
                      {route.inheritsFrom && (
                        <Text
                          fontSize="xs"
                          color={getThemeColor("text.secondary")}
                          fontStyle="italic"
                        >
                          {routeInheritanceHint(t, route)}
                        </Text>
                      )}
                    </VStack>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                      {t(`app.aiGateway.difficulty.tier.${route.defaultTier}`)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell minW="200px">
                    <Select
                      size="sm"
                      value={[tierByRoute[route.agentRoute] ?? route.tier]}
                      onValueChange={(d: { value: string[] }) =>
                        setTierByRoute((prev) => ({
                          ...prev,
                          [route.agentRoute]: d.value[0] as DifficultyTierName,
                        }))
                      }
                      items={tierOptions}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      </VStack>

      <VStack align="stretch" gap={3}>
        <Text fontSize="sm" fontWeight="semibold">
          {t("app.aiGateway.difficulty.dynamic.title")}
        </Text>
        <Text fontSize="xs" color={getThemeColor("text.secondary")}>
          {t("app.aiGateway.difficulty.dynamic.help")}
        </Text>
        <Flex gap={4} wrap="wrap" align="flex-start">
          <Box minW="240px" maxW="320px" flex="1">
            <Select
              label={t("app.aiGateway.difficulty.classifierTier")}
              value={[classifierTier]}
              onValueChange={(d: { value: string[] }) =>
                setClassifierTier(d.value[0] as DifficultyTierName)
              }
              items={tierOptions}
              disabled={!isDynamic}
              helperText={
                isDynamic
                  ? t("app.aiGateway.difficulty.classifierTierHelp")
                  : t("app.aiGateway.difficulty.dynamic.disabledHint")
              }
            />
          </Box>
          <Box minW="240px" maxW="320px" flex="1">
            <Select
              label={t("app.aiGateway.difficulty.dynamic.escalateMainTo")}
              value={[escalateMainTo]}
              onValueChange={(d: { value: string[] }) =>
                setEscalateMainTo(d.value[0] as DifficultyTierName)
              }
              items={tierOptions}
              disabled={!isDynamic || !escalationEnabled}
              helperText={
                isDynamic
                  ? t("app.aiGateway.difficulty.dynamic.escalateMainToHelp")
                  : t("app.aiGateway.difficulty.dynamic.disabledHint")
              }
            />
          </Box>
        </Flex>
        <Switch
          checked={escalationEnabled}
          disabled={!isDynamic}
          onCheckedChange={(e: { checked: boolean }) =>
            setEscalationEnabled(e.checked)
          }
        >
          <VStack align="start" gap={0}>
            <Text fontSize="sm">
              {t("app.aiGateway.difficulty.dynamic.escalation")}
            </Text>
            <Text fontSize="xs" color={getThemeColor("text.secondary")}>
              {isDynamic
                ? t("app.aiGateway.difficulty.dynamic.escalationHelp")
                : t("app.aiGateway.difficulty.dynamic.disabledHint")}
            </Text>
          </VStack>
        </Switch>
      </VStack>
    </VStack>
  );
};

export default DifficultyPanel;
